import { useEffect, type ComponentType, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { useStateMachine } from "@/core/arch/state_machine";
import { JobDetailBloc } from "@/application/tools/job_detail_bloc";
import { jobHistoryRepository } from "@/application/tools/job_history_repository";
import { isTerminal } from "@/application/tools/job_polling";
import { useNowTicker } from "@/hooks/useNowTicker";
import type { JobDetail } from "@/application/tools/job_history_repository";
import { ToolSheetShell } from "./ToolSheetShell";
import { JobStatusBadge } from "./JobStatusBadge";
import { ReconcilerTallyTable } from "./ReconcilerTallyTable";
import { groupCounters, scannedTotal } from "./reconciler_tallies";
import {
  chainLabel,
  formatAbsolute,
  formatDuration,
  formatJson,
} from "./job_display";

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-muted-foreground text-xs font-medium">{label}</dt>
      <dd className="text-foreground mt-0.5 text-sm">{children}</dd>
    </div>
  );
}

function Payload({ label, raw }: { label: string; raw: string | null }) {
  const formatted = formatJson(raw);
  if (!formatted) return null;

  return (
    <details className="border-border rounded-md border">
      <summary className="text-muted-foreground cursor-pointer px-3 py-2 text-xs font-medium">
        {label}
      </summary>
      <pre className="border-border bg-muted text-foreground max-h-80 overflow-auto border-t px-3 py-2 font-mono text-xs whitespace-pre">
        {formatted}
      </pre>
    </details>
  );
}

/** Reconcile reports per-reconciler counters, which read far better as a table. */
function ReconcileSummary({ job }: { job: JobDetail }) {
  const tallies = groupCounters(job.counters);
  if (tallies.length === 0) return null;

  return (
    <div className="space-y-2">
      <p className="text-muted-foreground text-xs">
        {scannedTotal(tallies, job.processed)} merchants scanned
      </p>
      <ReconcilerTallyTable tallies={tallies} />
    </div>
  );
}

/**
 * Job types with a purpose-built summary. Types absent from this map still get
 * the full record and raw payloads below, so a new runner needs no entry here.
 */
const STRUCTURED_SUMMARIES = new Map<string, ComponentType<{ job: JobDetail }>>(
  [["reconcile", ReconcileSummary]],
);

interface JobDetailSheetProps {
  jobId: string;
  onClose: () => void;
}

export function JobDetailSheet({ jobId, onClose }: JobDetailSheetProps) {
  const [state, bloc] = useStateMachine(
    () => new JobDetailBloc(jobHistoryRepository, jobId),
  );

  useEffect(() => {
    void bloc.load(jobId);
  }, [bloc, jobId]);

  const { job } = state;
  const Summary = job ? STRUCTURED_SUMMARIES.get(job.type) : undefined;
  const now = useNowTicker(job != null && !isTerminal(job.status));

  return (
    <ToolSheetShell title="Run details" onClose={onClose}>
      {state.status === "loading" && (
        <p className="text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading run…
        </p>
      )}

      {state.status === "missing" && (
        <div className="border-border bg-muted text-muted-foreground rounded-md border p-3 text-sm">
          This run no longer exists. Job rows expire, so older runs are removed
          automatically.
        </div>
      )}

      {state.status === "error" && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {state.error}
        </div>
      )}

      {job && (
        <div className="space-y-6">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
            <Field label="Status">
              <JobStatusBadge status={job.status} />
            </Field>
            <Field label="Duration">
              {formatDuration(job.startedAt, job.completedAt, now) ?? "—"}
            </Field>
            <Field label="Enqueued">{formatAbsolute(job.enqueuedAt)}</Field>
            <Field label="Started">{formatAbsolute(job.startedAt)}</Field>
            <Field label="Completed">{formatAbsolute(job.completedAt)}</Field>
            <Field label="Processed">
              <span className="tabular-nums">
                {job.processed}
                {job.total != null && ` / ${job.total}`}
              </span>
            </Field>
            <Field label="Triggered by">{job.triggeredBy}</Field>
            <Field label="Chain">{chainLabel(job.link)}</Field>
            <div className="col-span-2">
              <Field label="Job ID">
                <span className="text-muted-foreground font-mono text-xs">
                  {job.id}
                </span>
              </Field>
            </div>
            {job.chainRoot && (
              <div className="col-span-2">
                <Field label="Chain root">
                  <span className="text-muted-foreground font-mono text-xs">
                    {job.chainRoot}
                  </span>
                </Field>
              </div>
            )}
          </dl>

          {job.error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-xs font-medium text-red-700">Error</p>
              <pre className="mt-1 overflow-x-auto text-xs whitespace-pre-wrap text-red-600">
                {job.error}
              </pre>
            </div>
          )}

          {Summary && <Summary job={job} />}

          <div className="space-y-2">
            <Payload label="Input" raw={job.input} />
            <Payload label="Counters" raw={job.counters} />
            <Payload label="Result" raw={job.result} />
          </div>
        </div>
      )}
    </ToolSheetShell>
  );
}
